import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import logoImage from '../PA.svg';
import './Contact.css';
import '../Landing/Hero.css'

const SEARCH_API_URL = 'https://links.szapfs.org';


function Contact() {
  const navigate = useNavigate();
  const [isLoaded, setIsLoaded] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    organization: '',
    subject: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null); // 'loading', 'success', 'error'
  const [showConfirmation, setShowConfirmation] = useState(false);
  useEffect(() => {
    // Trigger animations after component mounts
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // --- Pure DOM search ---
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchTimerRef = useRef(null);

  const destroyDropdown = useCallback(() => {
    if (dropdownRef.current) { dropdownRef.current.remove(); dropdownRef.current = null; }
  }, []);

  const createDropdownContainer = useCallback(() => {
    destroyDropdown();
    if (!searchRef.current) return null;
    const rect = searchRef.current.getBoundingClientRect();
    const div = document.createElement('div');
    Object.assign(div.style, {
      position: 'fixed', top: `${rect.bottom + 4}px`, left: `${rect.left}px`,
      width: `${rect.width}px`, background: '#fff', borderRadius: '8px',
      border: '1px solid #e2e8f0', maxHeight: '350px', overflowY: 'auto', zIndex: '2147483647',
    });
    document.body.appendChild(div);
    dropdownRef.current = div;
    return div;
  }, [destroyDropdown]);

  const showMessage = useCallback((text) => {
    const div = createDropdownContainer();
    if (!div) return;
    const msg = document.createElement('div');
    Object.assign(msg.style, { padding: '12px 16px', color: '#94a3b8', fontSize: '0.9rem' });
    msg.textContent = text;
    div.appendChild(msg);
  }, [createDropdownContainer]);

  const buildDropdown = useCallback((results) => {
    if (!results.length) { showMessage('No results found'); return; }
    const div = createDropdownContainer();
    if (!div) return;
    results.forEach((result, i) => {
      const row = document.createElement('div');
      Object.assign(row.style, {
        padding: '10px 16px', cursor: 'pointer', display: 'flex',
        justifyContent: 'space-between', alignItems: 'center',
        borderBottom: i < results.length - 1 ? '1px solid #f1f5f9' : 'none',
      });
      row.onmouseenter = () => { row.style.background = '#f8fafc'; };
      row.onmouseleave = () => { row.style.background = '#fff'; };
      row.onclick = () => {
        if (searchInputRef.current) searchInputRef.current.value = '';
        destroyDropdown();
        navigate('/wbt/explore', { state: { searchResult: result } });
      };
      const label = document.createElement('span');
      Object.assign(label.style, { fontWeight: '500', color: '#1e293b', fontSize: '0.9rem' });
      label.textContent = result.label;
      const badge = document.createElement('span');
      Object.assign(badge.style, {
        fontSize: '0.75rem', color: '#94a3b8', background: '#f1f5f9',
        padding: '2px 8px', borderRadius: '4px', textTransform: 'capitalize',
        flexShrink: '0', marginLeft: '12px',
      });
      badge.textContent = result.tab;
      row.appendChild(label);
      row.appendChild(badge);
      div.appendChild(row);
    });
  }, [createDropdownContainer, showMessage, destroyDropdown, navigate]);

  const handleSearchChange = useCallback((e) => {
    const query = e.target.value;
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!query.trim()) { destroyDropdown(); return; }
    showMessage('Searching...');
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await fetch(`${SEARCH_API_URL}/search?q=${encodeURIComponent(query.trim())}`);
        if (!res.ok) throw new Error('Search failed');
        buildDropdown(await res.json());
      } catch (err) { console.error('Search error:', err); destroyDropdown(); }
    }, 300);
  }, [buildDropdown, destroyDropdown, showMessage]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target) &&
        (!dropdownRef.current || !dropdownRef.current.contains(e.target))) {
        destroyDropdown();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [destroyDropdown]);

  useEffect(() => () => destroyDropdown(), [destroyDropdown]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('loading');

    try {
      const response = await fetch('https://service9006.szapfs.org/contactus', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setShowConfirmation(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          organization: '',
          subject: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setSubmitStatus('error');
      setShowConfirmation(true);
    }
  };

  const closeConfirmation = () => {
    setShowConfirmation(false);
    setSubmitStatus(null);
  };

  return (
    <>
      {/* Navigation */}
      {/* Navigation */}
     <div className="navigation-system">
        <div className="main-nav-container">
          {/* Logo */}
          <a href="/" className="main-logo">
            <img src={logoImage} alt="PhenomeAtlas" className="main-logo-image" />
          </a>

          {/* Platform Menu */}
          <nav className="platform-menu">
            <a href="/wbt" className="platform-link">WBT-Atlas</a>
            {isMobile ? (
              <a href="/contact" className="platform-link">Contact</a>
            ) : (
              <>
              <a href="/contact" className="platform-link">Contact</a>
              </>
            )}
          </nav>

          {/* Search Bar */}
          {!isMobile && (
            <div className="integrated-search" ref={searchRef}>
              <div className="search-input-wrapper">
                <svg className="search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none">
                  <path d="M17.5 17.5L12.5 12.5M14.1667 8.33333C14.1667 11.555 11.555 14.1667 8.33333 14.1667C5.11167 14.1667 2.5 11.555 2.5 8.33333C2.5 5.11167 5.11167 2.5 8.33333 2.5C11.555 2.5 14.1667 5.11167 14.1667 8.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input
                  type="text"
                  ref={searchInputRef}
                  placeholder="Search genes, diseases, pathways..."
                  className="integrated-search-input"
                  onChange={handleSearchChange}
                />
                <button className="search-submit-btn-integrated">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M6.5 12L11 7.5L6.5 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          )}

                     {/* Mobile Menu Button - only show on mobile */}
           {isMobile && (
             <button className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`} onClick={toggleMobileMenu}>
               <span></span>
               <span></span>
               <span></span>
             </button>
           )}
        </div>
      </div>

      {/* Mobile Overlay */}
      {isMobile && (
        <>
          <div className={`mobile-overlay ${mobileMenuOpen ? 'open' : ''}`} onClick={closeMobileMenu}></div>
          
          {/* Mobile Sidebar */}
          <div className={`mobile-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
            <div className="mobile-sidebar-content">
              <a href="/wbt" className="mobile-nav-link" onClick={closeMobileMenu}>WBT-Atlas</a>
              <a href="/contact" className="mobile-nav-link" onClick={closeMobileMenu}>Contact</a>
              <a href="https://mphenome.org" className="mobile-nav-link" onClick={closeMobileMenu}>About 1M-PI</a>
            </div>
          </div>
        </>
      )}


<div className="contact-page">

{/* Main Content */}
<div className="contact-main-content">
  <div className="contact-container">
    <motion.div 
      className={`contact-header ${isLoaded ? 'animate-header' : ''}`}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      <h1 className="contact-title">Contact Us</h1>
      <p className="contact-subtitle">
        Get in touch with our research teams at SciLifeLab and King's College London
      </p>
    </motion.div>

    <div className="contact-locations">
      {/* SciLifeLab Section */}
      <motion.div 
        className={`contact-location-card ${isLoaded ? 'animate-card-1' : ''}`}
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
      >
        <div className="location-header">
          <h2 className="location-title">SciLifeLab</h2>
          <div className="location-badge">Sweden</div>
        </div>

        <div className="address-section">
          <h3 className="address-type">Email Address</h3>
          <div className="address-content">
            <p><a href="mailto:Adil@scilifelab.se" className="email-link">adil@scilifelab.se</a></p>
          </div>
        </div>
        <div className="address-section">
          <h3 className="address-type">Visiting Address</h3>
          <div className="address-content">
            <p>Science for Life Laboratory</p>
            <p>Tomtebodavägen 23A</p>
            <p>Solna, Sweden, SE-17165</p>
          </div>
        </div>

        <div className="address-section">
          <h3 className="address-type">Mailing Address</h3>
          <div className="address-content">
            <p>Science for Life Laboratory</p>
            <p>Box 1031</p>
            <p>Solna, Sweden, SE-17121</p>
          </div>
        </div>

      </motion.div>

      {/* KCL Section */}
      <motion.div 
        className={`contact-location-card ${isLoaded ? 'animate-card-2' : ''}`}
        initial={{ opacity: 0, x: 50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        <div className="location-header">
          <h2 className="location-title">King's College London</h2>
          <div className="location-badge">United Kingdom</div>
        </div>
<div className="address-section">
          <h3 className="address-type">Email Address</h3>
          <div className="address-content">
            <p><a href="mailto:Adil.Mardinoglu@kcl.ac.uk" className="email-link">adil.mardinoglu@kcl.ac.uk</a></p>
          </div>
        </div>
        <div className="address-section">
          <h3 className="address-type">Visiting Address</h3>
          <div className="address-content">
            <p>Centre for Host-Microbiome Interactions</p>
            <p>Faculty of Dentistry, Oral & Craniofacial Sciences</p>
            <p>King's College London</p>
            <p>Floor 18, Tower Wing</p>
            <p>Guy's & St Thomas' Hospitals</p>
            <p>Great Maze Pond, London, SE1 9RT</p>
            <p>United Kingdom</p>
          </div>
        </div>

        <div className="address-section">
          <h3 className="address-type">Mailing Address</h3>
          <div className="address-content">
            <p>Centre for Host-Microbiome Interactions</p>
            <p>Faculty of Dentistry, Oral & Craniofacial Sciences</p>
            <p>King's College London</p>
            <p>Floor 18, Tower Wing</p>
            <p>Guy's & St Thomas' Hospitals</p>
            <p>Great Maze Pond, London, SE1 9RT</p>
            <p>United Kingdom</p>
          </div>
        </div>

    
      </motion.div>
    </div>

    {/* Contact Form Section */}
    <motion.div 
      className={`contact-form-section ${isLoaded ? 'animate-form' : ''}`}
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: 0.8 }}
    >
      <div className="contact-form-card">
        <div className="form-header">
          <h2 className="form-title">Send us a Message</h2>
          <p className="form-subtitle">Have a question or want to learn more? We'd love to hear from you.</p>
        </div>

        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="firstName" className="form-label">First Name</label>
              <input 
                type="text" 
                id="firstName" 
                name="firstName" 
                className="form-input" 
                value={formData.firstName}
                onChange={handleInputChange}
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="lastName" className="form-label">Last Name</label>
              <input 
                type="text" 
                id="lastName" 
                name="lastName" 
                className="form-input" 
                value={formData.lastName}
                onChange={handleInputChange}
                required 
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email" className="form-label">Email Address</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                className="form-input" 
                value={formData.email}
                onChange={handleInputChange}
                required 
              />
            </div>
            <div className="form-group">
              <label htmlFor="organization" className="form-label">Organization</label>
              <input 
                type="text" 
                id="organization" 
                name="organization" 
                className="form-input" 
                value={formData.organization}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="subject" className="form-label">Subject</label>
            <input 
              type="text" 
              id="subject" 
              name="subject" 
              className="form-input" 
              value={formData.subject}
              onChange={handleInputChange}
              required 
            />
          </div>

          <div className="form-group">
            <label htmlFor="message" className="form-label">Message</label>
            <textarea 
              id="message" 
              name="message" 
              rows="5" 
              className="form-textarea" 
              value={formData.message}
              onChange={handleInputChange}
              required
            ></textarea>
          </div>

          <button 
            type="submit" 
            className={`form-submit-btn ${submitStatus === 'loading' ? 'loading' : ''}`}
            disabled={submitStatus === 'loading'}
          >
            {submitStatus === 'loading' ? (
              <>
                <span className="loading-spinner1"></span>
                Sending...
              </>
            ) : (
              'Send Message'
            )}
          </button>
        </form>
      </div>
    </motion.div>
   
  </div>
</div>

{/* Confirmation Modal */}
{showConfirmation && (
  <div className="confirmation-overlay" onClick={closeConfirmation}>
    <motion.div 
      className="confirmation-modal"
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8, y: 20 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="confirmation-content">
        {submitStatus === 'success' ? (
          <>
            <div className="success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12l2 2 4-4" />
                <circle cx="12" cy="12" r="10" />
              </svg>
            </div>
            <h3 className="confirmation-title">Message Sent Successfully!</h3>
            <p className="confirmation-message">
              Thank you for reaching out! We've received your message and will get back to you as soon as possible.
            </p>
          </>
        ) : (
          <>
            <div className="error-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>
            <h3 className="confirmation-title">Oops! Something went wrong</h3>
            <p className="confirmation-message">
              There was an issue sending your message. Please try again or contact us directly via email.
            </p>
          </>
        )}
        <button className="confirmation-btn" onClick={closeConfirmation}>
          Close
        </button>
      </div>
    </motion.div>
        </div>
      )}
</div>
    </>
  );
}

export default Contact;
